import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { strCode } from "../../../../../consts";
import { notificationService, t } from "../../../../../services";
import { appsUtil, dateUtil, dropdownUtil } from "../../../../../utils";
import { useGetRoleConfigOptions } from "../../hooks/useGetRoleConfigOptions";

import {
  DateRangeInput,
  DropdownInput,
  FlexContainers,
  SmallButton,
  WrapperContainer
} from "../../../../../components";
import { DateRangeInputWrapper } from "./styled";

export const RoleForm = ({
  roleConfig,
  selectedRolesList,
  isPrepopulated,
  hasError,
  disabled,
  onClose,
  onSaveRole
}) => {
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [isValid, setIsValid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const { appOptions, marketOptions, locationOptions, roleOptions } = useGetRoleConfigOptions({
    isPrepopulated,
    selectedApplication,
    selectedMarkets,
    selectedRole,
    selectedRolesList,
    setSelectedMarkets,
    setSelectedLocation,
  });

  const hasRoleError = useMemo(() => {
    return hasError && selectedRole && selectedRole?.id === roleConfig?.role?.id;
  }, [hasError, selectedRole, roleConfig?.role]);

  const isExipred = useMemo(() => {
    if (roleConfig?.to) {
      const dateDiff = dateUtil.getDateDifferenceInMilliseconds(new Date(), roleConfig?.to);

      return dateDiff > 0;
    }

    return false;
  }, [roleConfig]);

  useEffect(() => {
    if (roleConfig?.application?.id !== selectedApplication?.id) {
      setSelectedApplication(roleConfig.application);
    }
  }, [roleConfig?.application]);

  useEffect(() => {
    const valid =
      selectedApplication &&
      selectedRole &&
      selectedMarkets?.length &&
      fromDate &&
      toDate &&
      !hasRoleError;

    const errorFields = {
      selectedApplication: !selectedApplication,
      selectedRole: !selectedRole || hasRoleError,
      selectedMarkets: !selectedMarkets?.length,
      fromDate: !fromDate,
      toDate: !toDate
    };

    setMissingFields(errorFields);
    setIsValid(valid);
  }, [
    selectedApplication,
    selectedMarkets,
    selectedLocation,
    selectedRole,
    fromDate,
    toDate,
    hasRoleError
  ]);

  const getConfigData = () => {
    const data = {
      id: roleConfig?.id,
      application: selectedApplication,
      market: selectedMarkets,
      location: selectedLocation,
      role: selectedRole,
      from: fromDate,
      to: toDate
    };

    return data;
  };

  useEffect(() => {
    prepopulateData();
  }, [roleConfig, appOptions?.length, roleOptions?.length]);

  useEffect(() => {
    if (!selectedLocation) {
      setSelectedLocation(roleConfig?.location);
    }
  }, [locationOptions]);

  const prepopulateData = () => {
    if (roleConfig) {
      if (!selectedApplication) {
        setSelectedApplication(roleConfig?.application);
      }

      if (!selectedRole) {
        setSelectedRole(roleConfig?.role);
      }

      setSelectedMarkets(roleConfig?.market ?? []);

      if (!selectedLocation) {
        setSelectedLocation(roleConfig?.location);
      }

      setFromDate(roleConfig?.from);
      setToDate(roleConfig?.to);
    }
  };

  useEffect(() => {
    setSelectedLocation(null);
  }, [selectedMarkets?.length]);

  useEffect(() => {
    if (selectedRole && selectedApplication?.id !== roleConfig?.application?.id) {
      const shouldResetRole = !roleOptions?.find((el) => el.id === selectedRole.id);

      if (shouldResetRole) {
        setSelectedRole(null);
      }
    }
  }, [selectedApplication, roleOptions]);

  const resetFormData = () => {
    setSelectedApplication(null);
    setSelectedMarkets([]);
    setSelectedLocation(null);
    setSelectedRole(null);

    setFromDate(null);
    setToDate(null);

    setShowErrors(false);
    setMissingFields({});
  };

  const onCancel = () => {
    resetFormData();
    onClose();
  };

  const onSubmit = () => {
    if (!showErrors) {
      setShowErrors(true);
    }

    if (!isValid) {
      notificationService.showError({
        title: t(strCode.MISSING_FIELDS),
        message: t(strCode.MISSING_FIELDS_MESSAGE)
      });
    } else {
      const data = getConfigData();

      onSaveRole(data);
      onCancel();
    }
  };

  return (
    <WrapperContainer Container={FlexContainers.FlexColumnStart} width={"382px"} gap={16}>
      <DropdownInput
        id="role-app-selector"
        title={t(strCode.APPLICATION_TITLE)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        options={appOptions.filter(
          (x) => x.id === selectedApplication?.id || appsUtil.isAppActive(x.id)
        )}
        selectedOption={selectedApplication}
        onSelectOption={(id) => {
          setSelectedApplication(appOptions.find((opt) => opt.id === id));
        }}
        hasError={showErrors && missingFields?.selectedApplication}
        disabled={disabled}
      />

      <DropdownInput
        id="role-selector"
        title={t(strCode.ROLE_TITLE)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        options={roleOptions}
        selectedOption={selectedRole}
        onSelectOption={(id) => {
          setSelectedRole(roleOptions.find((opt) => opt.id === id));
        }}
        hasError={hasRoleError || (showErrors && missingFields?.selectedRole)}
        disabled={!selectedApplication || disabled}
      />

      <DropdownInput
        id="market-selector"
        title={t(strCode.MARKET_TITLE)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        options={marketOptions}
        selectedOptions={selectedMarkets}
        onSelectOption={(id) => {
          setSelectedMarkets(
            dropdownUtil.updateSelectedOptions(id, selectedMarkets, marketOptions, true)
          );
        }}
        hasError={showErrors && missingFields?.selectedMarkets}
        disabled={!selectedApplication || disabled}
        showCheckboxes
      />

      <DropdownInput
        id="locations-selector"
        title={t(strCode.LOCATIONS_TITLE)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        warningMsg={t(
          selectedMarkets?.length > 1
            ? strCode.DISABLED_LOCATION_INFO
            : strCode.OPTIONAL_LOCATION_INFO
        )}
        options={locationOptions}
        selectedOption={selectedLocation}
        onSelectOption={(id) => {
          setSelectedLocation(locationOptions.find((opt) => opt.id === id));
        }}
        disabled={selectedMarkets?.length !== 1 || disabled}
      />

      <DateRangeInputWrapper Container={FlexContainers.FlexRowStart} gap={16}>
        <DateRangeInput
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          noTime
          errors={{
            start: showErrors && missingFields?.fromDate,
            end: (showErrors && missingFields?.toDate) || isExipred
          }}
          disabled={disabled}
        />
      </DateRangeInputWrapper>

      <WrapperContainer Container={FlexContainers.FlexRowEnd} width={"100%"} gap={8}>
        <SmallButton secondary onClick={onCancel}>
          {t(strCode.DISCARD_BTN)}
        </SmallButton>
        <SmallButton onClick={onSubmit}>{t(strCode.SUBMIT_BTN)}</SmallButton>
      </WrapperContainer>
    </WrapperContainer>
  );
};

RoleForm.propTypes = {
  roleConfig: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    application: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }),
    market: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
    ),
    location: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }),
    role: PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired }),
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date)
  }),
  selectedRolesList: PropTypes.arrayOf(
    PropTypes.shape({ application: PropTypes.string, role: PropTypes.string })
  ),
  isPrepopulated: PropTypes.bool,
  hasError: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaveRole: PropTypes.func.isRequired
};
